import {Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {forkJoin, of} from "rxjs";
import {
  AppsGroupsFiles,
  AppsGroupsRulesPreview,
  NetscopeApiService,
  Success,
  Response
} from "@app/services/netscope-api.service";
import {AppsGroupMembersDisplayMode, AppsGroups, MemberRoles, defaultDisplayMode, AppsGroupsData} from "@app/netscope/model/data-models";
import {
  AppsGroupsComboboxComponent
} from "@app/netscope/netscope-common-flowview/apps-groups-combobox/apps-groups-combobox.component";
import {
  AppGroupState,
  applyDisplayMode,
  includedAppsGroupKey,
  memberVisibilityKey
} from "@app/netscope/netscope-common-flowview/netscope-common-flowview.component";
import {AppsGroupsInventory} from "@app/netscope/netscope-common-flowview/misc/AppsGroupsInventory";

/**
 * An apps group as edited in the panel: on top of the group itself, it carries the transient state of its
 * edition form (validity of the name, resources being picked in the combobox).
 */
type EditedAppsGroup = AppsGroups & {
  nameIsValid?: boolean;
  selectedResources?: any[];
  // Name the group had before the user started typing in its name input: the groups including it point to it
  // by name, and those references are followed as it is renamed
  previousName?: string;
  // Patterns of the members rules, shown as chips: 'members_rules' is built from them
  includePatterns?: string[];
  excludePatterns?: string[];
  // Pattern being typed, and the kind of patterns it is added to
  patternDraft?: string;
  patternKind?: PatternKind;
  // Whether the members rules section is unfolded, the groups having rules showing it by default
  rulesOpen?: boolean;
  rulesPreview?: MembersRulesPreview;
};

type PatternKind = "include" | "exclude";

/**
 * What the members rules of an apps group match in the latest inventory, as the appliance last answered while
 * they were typed.
 */
type MembersRulesPreview = {
  loading: boolean;
  failed: boolean;
  total?: number;
  members?: { uuid: string, name: string, type: string }[];
};

// Limits of the members rules, which the appliance checks as well
const MAX_PATTERNS_COUNT = 50;
const MAX_PATTERN_LENGTH = 200;
// Names of the matched resources listed in the preview, the other ones being counted
const PREVIEWED_NAMES_COUNT = 8;
const PREVIEW_DELAY_MS = 300;

/**
 * Splits the patterns typed by the user on the commas, except the ones inside brackets: '[,]' is the way to
 * match a comma in a name. The blank patterns are dropped.
 */
export function parsePatterns(text: string): string[] {
  let patterns = [];
  let current = "";
  let insideBrackets = false;

  for (let character of text ?? "") {
    if (character == "," && !insideBrackets) {
      patterns.push(current);
      current = "";
      continue;
    }
    if (character == "[") {
      insideBrackets = true;
    } else if (character == "]") {
      insideBrackets = false;
    }
    current += character;
  }
  patterns.push(current);

  return patterns.map((pattern) => pattern.trim()).filter((pattern) => pattern !== "");
}

// Whether the text ends inside brackets: a comma typed there belongs to the pattern ('[,]'), it separates nothing
export function endsInsideBrackets(text: string): boolean {
  let insideBrackets = false;

  for (let character of text ?? "") {
    if (character == "[") {
      insideBrackets = true;
    } else if (character == "]") {
      insideBrackets = false;
    }
  }

  return insideBrackets;
}

// Pattern matching exactly the given name: the characters a glob gives a meaning to, and the comma separating
// the patterns, are put between brackets
export function exactNamePattern(name: string): string {
  return name.replace(/[*?\[,]/g, "[$&]");
}

/**
 * Floating accordions holding everything about the apps groups of the apps groups view. They are stacked:
 *
 *  - the first one lists the apps groups files and lets the user pick, create or delete one,
 *  - the second one shows up under it once a file is selected, and is dedicated to the apps groups of that
 *    file: it lets the user add, edit and delete them, and choose how each of them is displayed (hidden or
 *    not, folding mode, hidden members).
 *
 * The display choices are applied as soon as they are made: they only live in the state maps owned by the
 * flow view, which the renderer reads back. The edition of the file content is applied on an explicit save,
 * as it is written back to the API.
 */
@Component({
  standalone: false,
  selector: 'app-apps-groups-panel',
  templateUrl: './apps-groups-panel.component.html',
  styleUrl: './apps-groups-panel.component.css'
})
export class AppsGroupsPanelComponent implements OnInit, OnChanges {

  // Apps groups file currently displayed by the flow view (its 'appsGroupsMode')
  @Input()
  selectedFile: string;

  // Display state shared with the flow view and read back by AppsGroupsRendererJointJs. The maps and the set
  // are mutated in place so that the renderer always sees the last choices of the user.
  @Input()
  appGroupStates: Map<string, AppGroupState>;

  @Input()
  vmVisibilityStates: Map<string, boolean>;

  // Role played by every member in the flows, filled by the renderer: the display modes below are presets
  // over these roles, and an empty map simply means that nothing was rendered yet.
  @Input()
  memberRoles: Map<string, MemberRoles> = new Map();

  @Input()
  hiddenAppsGroups: Set<string>;

  // Whether the floating accordions are shown at all: toggled from the toolbar of the apps groups view
  @Input()
  open: boolean = false;

  @Output()
  openChange: EventEmitter<boolean> = new EventEmitter();

  @Output()
  selectedFileChange: EventEmitter<string> = new EventEmitter();

  // Emitted when a display choice changed (group hidden, folding mode, member hidden): the view is redrawn
  @Output()
  viewStateChanged: EventEmitter<void> = new EventEmitter();

  // Emitted when the content of the file was written back to the API
  @Output()
  appsGroupsSaved: EventEmitter<string> = new EventEmitter();

  // Files accordion
  generatedFiles: string[] = [];
  customFiles: string[] = [];
  filesExpanded: boolean = true;
  generatedExpanded: boolean = true;
  customExpanded: boolean = true;
  newFileName: string = "";
  newFileNameIsValid: boolean = true;
  // Whether the files were listed: until then, whether the displayed file is one DC Netscope generates is
  // unknown, and nothing is offered for edition
  filesLoaded: boolean = false;
  fileToDelete: string;
  showDeleteFileModal: boolean = false;

  // Groups accordion, dedicated to the apps groups of the selected file
  appsGroupsData;
  appGroups: EditedAppsGroup[] = [];
  // Apps groups of the other files, indexed by file: an apps group can include one of another file, and the
  // groups of that file have to be offered, walked for cycles and read for their members. They are never
  // edited here, only the groups of the displayed file are.
  appsGroupsByFile: Map<string, any[]> = new Map();
  // Resources the groups are built from, and the names of their members
  inventory: AppsGroupsInventory = new AppsGroupsInventory();
  groupsExpanded: boolean = true;
  // The expanded groups are held by reference and not by name: the name of a group changes as the user types
  // in its name input, and keying that state on it would fold the group being renamed at every keystroke.
  expandedGroups: Set<EditedAppsGroup> = new Set();
  // Group the flow view asked to focus on while its file was still loading, expanded once it shows up
  pendingExpandedGroupName: string;
  // Group whose '⋮' menu is open, and group whose matched VMs and hosts are listed in a popover: both close
  // on any click elsewhere
  menuGroup: EditedAppsGroup;
  previewedGroup: EditedAppsGroup;

  isLoadingFiles: boolean = false;
  isLoadingGroups: boolean = false;
  isPushing: boolean = false;
  saveFailed: boolean = false;
  filesFailureMode: boolean = false;
  groupsFailureMode: boolean = false;

  constructor(private netscopeService: NetscopeApiService) {
  }

  ngOnInit() {
    this.reloadFiles();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["selectedFile"] != undefined) {
      this.reloadGroups();
    }
  }

  @HostListener('document:click')
  closePopups() {
    this.menuGroup = undefined;
    this.previewedGroup = undefined;
  }

  toggleMenu = (appGroup: EditedAppsGroup) => {
    this.menuGroup = this.menuGroup === appGroup ? undefined : appGroup;
  }

  toggleOpen = () => {
    this.open = !this.open;
    this.openChange.emit(this.open);
  }

  /**
   * Called by the flow view when the user clicks the cog of an app group of the graph: the accordions are
   * shown, folded on the sole settings of that group.
   */
  focusOnGroup = (name: string) => {
    this.filesExpanded = false;
    this.groupsExpanded = true;
    this.expandedGroups.clear();
    this.pendingExpandedGroupName = undefined;

    let focusedGroup = this.appGroups.find((appGroup) => appGroup.name == name);
    if (focusedGroup != undefined) {
      this.expandedGroups.add(focusedGroup);
    } else {
      // The file is still loading: the group is expanded as soon as its groups are refreshed
      this.pendingExpandedGroupName = name;
    }

    if (!this.open) {
      this.toggleOpen();
    }
  }

  // ---------------------------------------------------------------- Files

  reloadFiles = () => {
    this.isLoadingFiles = true;

    this.netscopeService.getAppsGroupsFiles().subscribe((response) => {
      if (response instanceof Success) {
        let appsGroupsFiles: AppsGroupsFiles = response.element;

        this.generatedFiles = appsGroupsFiles.files
          .filter((file) => appsGroupsFiles.generatedFiles.includes(file));
        this.customFiles = appsGroupsFiles.files
          .filter((file) => !appsGroupsFiles.generatedFiles.includes(file));

        this.filesFailureMode = false;
        this.filesLoaded = true;
      } else {
        this.filesFailureMode = true;
      }

      this.isLoadingFiles = false;
    });
  }

  selectFile = (file: string) => {
    if (file == this.selectedFile) {
      return;
    }

    this.selectedFile = file;
    // The groups of the newly selected file are loaded by ngOnChanges, once the flow view pushed the file back
    this.selectedFileChange.emit(file);
  }

  // The same characters as the ones accepted by the API
  isValidFileName = (name: string): boolean => {
    return /^[A-Za-z0-9_-]+$/.test(name);
  }

  createFile = () => {
    let name = this.newFileName.trim();

    this.newFileNameIsValid = this.isValidFileName(name);
    if (!this.newFileNameIsValid) {
      return;
    }

    this.isLoadingFiles = true;

    this.netscopeService.createAppsGroupsFile(name).subscribe((response) => {
      this.newFileName = "";
      this.reloadFiles();

      if (response instanceof Success) {
        this.selectFile(name);
      }
    });
  }

  askToDeleteFile = (file: string) => {
    this.fileToDelete = file;
    this.showDeleteFileModal = true;
  }

  deleteFile = () => {
    let file = this.fileToDelete;

    this.showDeleteFileModal = false;
    this.isLoadingFiles = true;

    this.netscopeService.deleteAppsGroupsFile(file).subscribe((response) => {
      // The deleted file cannot stay selected, the first remaining file is used instead
      if (file == this.selectedFile) {
        let remainingFile = this.generatedFiles.concat(this.customFiles)
          .filter((f) => f != file)[0];

        if (remainingFile != undefined) {
          this.selectFile(remainingFile);
        }
      }

      this.reloadFiles();
    });
  }

  // ---------------------------------------------------------------- Groups

  reloadGroups = () => {
    if (this.selectedFile == undefined) {
      return;
    }

    this.isLoadingGroups = true;
    this.groupsFailureMode = false;

    this.inventory.load(this.netscopeService).then((loaded) => {
      if (!loaded) {
        this.groupsFailureMode = true;
        this.isLoadingGroups = false;
        return;
      }

      this.netscopeService.getAppsGroups(this.selectedFile).subscribe((appsGroupsDataResponse: Response<AppsGroupsData>) => {
        if (appsGroupsDataResponse instanceof Success) {
          this.appsGroupsData = appsGroupsDataResponse.element;
        } else {
          this.groupsFailureMode = true;
          this.isLoadingGroups = false;
          return;
        }

        this.refreshAppGroups();
        this.isLoadingGroups = false;

        // The groups of the other files are only needed to offer them and to read the members of the ones
        // that are included: they are loaded on the side, the displayed file is not waiting for them
        this.reloadOtherFilesGroups();
      });
    });
  }

  /**
   * Loads the apps groups of every other file, as an apps group can include one of another file. Nothing is
   * editable there: those groups are offered by the combobox, walked to find the members they bring, and
   * checked so that two groups cannot end up including each other.
   */
  reloadOtherFilesGroups = () => {
    this.netscopeService.getAppsGroupsFiles().subscribe((filesResponse) => {
      if (!(filesResponse instanceof Success)) {
        return;
      }

      let otherFiles = ((filesResponse.element as AppsGroupsFiles).files ?? [])
        .filter((file) => file != this.selectedFile);

      if (otherFiles.length == 0) {
        this.appsGroupsByFile = new Map();
        return;
      }

      forkJoin(otherFiles.map((file) => this.netscopeService.getAppsGroups(file)))
        .subscribe((responses: Response<AppsGroupsData>[]) => {
          let appsGroupsByFile: Map<string, any[]> = new Map();

          responses.forEach((response, index) => {
            if (response instanceof Success) {
              appsGroupsByFile.set(otherFiles[index], response.element?.apps_groups ?? []);
            }
          });

          this.appsGroupsByFile = appsGroupsByFile;
        });
    });
  }

  // See AppsGroupsInventory
  get resourcesByUuid(): Map<string, any> {
    return this.inventory.resourcesByUuid;
  }

  get selectableResources(): any[] {
    return this.inventory.selectableResources;
  }

  resourceBadgeClass = (resourceType: string): string => this.inventory.resourceBadgeClass(resourceType);
  memberType = (uuid: string): string => this.inventory.memberType(uuid);
  typedAddressResource = (address: string) => this.inventory.typedAddressResource(address);
  resourceLabel = (uuid: string): string => this.inventory.resourceLabel(uuid);

  refreshAppGroups = () => {
    if (this.appsGroupsData.apps_groups == undefined) {
      this.appsGroupsData.apps_groups = [];
    }

    for (let appGroup of this.appsGroupsData.apps_groups) {
      // A group holding nothing but other apps groups has no members of its own
      appGroup.vms = appGroup.vms ?? [];

      for (let vm of appGroup.vms) {
        vm.name = this.resourceLabel(vm.uuid);
      }
      appGroup.vms = appGroup.vms.sort((a, b) => a.name.localeCompare(b.name));

      // The members matched by the rules are named the same way, to be listed next to the picked ones
      let editedAppGroup = appGroup as EditedAppsGroup;
      editedAppGroup.matched_vms = (editedAppGroup.matched_vms ?? [])
        .map((member) => ({...member, name: this.resourceLabel(member.uuid)}))
        .sort((a, b) => a.name.localeCompare(b.name));

      // The patterns being edited are kept across the refreshes, only a group coming from the file gets them
      if (editedAppGroup.includePatterns == undefined) {
        editedAppGroup.includePatterns = [...(editedAppGroup.members_rules?.include ?? [])];
        editedAppGroup.excludePatterns = [...(editedAppGroup.members_rules?.exclude ?? [])];
      }
    }

    // Unlike the former edition side panel, the groups tracking a VMware tag are listed too: their content is
    // not editable, but their display can be tuned as the one of any other group.
    this.appGroups = this.appsGroupsData.apps_groups
      .sort((a, b) => a.name.localeCompare(b.name));

    for (let appGroup of this.appGroups) {
      appGroup.previousName = appGroup.name;
    }

    // A reload builds brand new group objects: the groups that were expanded are matched back by name, so
    // that saving the file does not fold the group being edited.
    let expandedNames = new Set(Array.from(this.expandedGroups).map((appGroup) => appGroup.name));
    if (this.pendingExpandedGroupName != undefined) {
      expandedNames.add(this.pendingExpandedGroupName);
      this.pendingExpandedGroupName = undefined;
    }
    this.expandedGroups = new Set(this.appGroups.filter((appGroup) => expandedNames.has(appGroup.name)));
  }

  /**
   * Whether an apps groups file is one DC Netscope builds itself. Such a file is shown read-only: the
   * appliance rewrites it as it refreshes the infrastructure, and anything saved over it would be lost.
   * A custom file can still include its apps groups, which is the way to build on them.
   */
  isReadOnlyFile = (file: string = this.selectedFile): boolean => {
    return file != undefined && this.generatedFiles.includes(file);
  }

  // The display choices stay available on a read-only file: they belong to the view, not to the file
  isEditableFile = (): boolean => {
    return this.selectedFile != undefined && this.filesLoaded && !this.isReadOnlyFile();
  }

  // Whether the members of an apps group can be edited here: the ones of a generated file cannot. A group
  // following a VMware tag includes the group of the tag held by the generated 'tags' file instead
  isEditable = (appGroup): boolean => {
    return this.isEditableFile();
  }

  isGroupExpanded = (appGroup): boolean => {
    return this.expandedGroups.has(appGroup);
  }

  toggleGroup = (appGroup) => {
    if (this.expandedGroups.has(appGroup)) {
      this.expandedGroups.delete(appGroup);
    } else {
      this.expandedGroups.add(appGroup);
    }
  }

  addAppGroup = () => {
    if (!this.isEditableFile()) {
      return;
    }

    let newAppGroup = {
      name: "new_app_group",
      vms: []
    } as EditedAppsGroup;
    this.appsGroupsData.apps_groups.push(newAppGroup);
    this.refreshAppGroups();
    this.expandedGroups.add(newAppGroup);
  }

  deleteAppGroup = (appGroup) => {
    if (!this.isEditable(appGroup)) {
      return;
    }

    this.appsGroupsData.apps_groups = this.appsGroupsData.apps_groups.filter((a) => a !== appGroup);

    // The groups of the displayed file including the deleted one would keep a reference to a group that is
    // not there anymore. The groups of the other files are not saved from here: theirs is shown as missing.
    for (let otherAppGroup of this.appsGroupsData.apps_groups) {
      if (otherAppGroup.included_apps_groups == undefined) {
        continue;
      }
      otherAppGroup.included_apps_groups = otherAppGroup.included_apps_groups
        .filter((included) => !(included.file == undefined && included.name === appGroup.name));
    }

    this.refreshAppGroups();
  }

  checkAppGroupName = (appGroup) => {
    const nameRegex = /^[A-Za-z0-9_]+$/;
    appGroup.nameIsValid = nameRegex.test(appGroup.name);
    return appGroup.nameIsValid;
  }

  /**
   * Called as the user types in the name of an apps group: an inclusion points to a group by name, so the
   * groups including the renamed one are made to point to its new name, or they would lose their members.
   */
  appGroupNameChanged = (appGroup: EditedAppsGroup) => {
    this.checkAppGroupName(appGroup);

    let previousName = appGroup.previousName;
    appGroup.previousName = appGroup.name;

    if (previousName == undefined || previousName === appGroup.name) {
      return;
    }

    for (let otherAppGroup of this.appGroups) {
      for (let included of otherAppGroup.included_apps_groups ?? []) {
        // Only the inclusions pointing inside the displayed file: the ones naming another file point to a
        // group of that file, which happens to bear the same name. The apps groups of the other files are
        // not saved from here, so a group they include cannot be renamed under them either.
        if (included.file == undefined && included.name === previousName) {
          included.name = appGroup.name;
        }
      }
    }
  }

  /**
   * Feeds the combobox of an apps group: every resource that can take part in a flow, whatever its kind, the
   * IP address or the network the user may be typing, and the apps groups of every file, which can be
   * included in this one. The apps groups come first, they are the shortest list and the one the user scans
   * for, and the groups of the displayed file open them.
   */
  fetchResources = (appGroup) => (filter = '', combobox: AppsGroupsComboboxComponent) => {
    // The combobox calls its generator without itself on focus, and there is nothing to feed then
    if (combobox == undefined) {
      return;
    }

    combobox.loadingResources = true;

    let matches = (text: string): boolean => {
      return text != undefined && text.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
    };

    let resources = this.selectableResources
      .filter((resource) => filter === ''
        || matches(resource.name) || matches(resource.address) || matches(resource.uuid));

    let typedAddressResource = this.typedAddressResource(filter);
    if (typedAddressResource != undefined
        && resources.filter((resource) => resource.uuid == typedAddressResource.uuid).length == 0) {
      resources = [typedAddressResource].concat(resources);
    }

    let includableAppGroups = this.includableAppGroups(appGroup)
      .filter((candidate) => filter === '' || matches(candidate.name) || matches(candidate.file))
      .map((candidate) => {
        return {
          name: candidate.name,
          // The file is shown next to the name, the way the address of a resource is
          address: candidate.file == this.selectedFile ? undefined : candidate.file,
          file: candidate.file,
          resourceType: "apps group",
          isAppsGroup: true
        };
      });

    combobox.asyncResources$ = of(includableAppGroups.concat(resources));
    combobox.loadingResources = false;
  }

  vmSelectionChanged = (appGroup, event) => {
    for (let resource of appGroup.selectedResources) {
      if (resource.isAppsGroup) {
        this.includeAppGroup(appGroup, resource.name, resource.file);
        continue;
      }

      // A resource can only belong once to a given apps group
      if (appGroup.vms.filter((vm) => vm.uuid == resource.uuid).length > 0) {
        continue;
      }
      appGroup.vms.push({
        uuid: resource.uuid
      });
    }
    appGroup.selectedResources = [];
    event.model = [];
    this.refreshAppGroups();
  }

  deleteVmFromAppGroup = (appGroup, vm) => {
    appGroup.vms = appGroup.vms.filter((vm2) => vm2.uuid !== vm.uuid);
  }

  // ------------------------------------------------- Members matched by rules

  private previewTimers: Map<EditedAppsGroup, any> = new Map();
  private previewRequestIds: Map<EditedAppsGroup, number> = new Map();

  hasIncludePatterns = (appGroup: EditedAppsGroup): boolean => {
    return (appGroup.includePatterns ?? []).length > 0;
  }

  hasExcludePatterns = (appGroup: EditedAppsGroup): boolean => {
    return (appGroup.excludePatterns ?? []).length > 0;
  }

  hasMembersRules = (appGroup: EditedAppsGroup): boolean => {
    return this.hasIncludePatterns(appGroup) || this.hasExcludePatterns(appGroup);
  }

  isRulesOpen = (appGroup: EditedAppsGroup): boolean => {
    return appGroup.rulesOpen ?? this.hasMembersRules(appGroup);
  }

  toggleRules = (appGroup: EditedAppsGroup) => {
    appGroup.rulesOpen = !this.isRulesOpen(appGroup);
  }

  patternKind = (appGroup: EditedAppsGroup): PatternKind => {
    return appGroup.patternKind ?? "include";
  }

  setPatternKind = (appGroup: EditedAppsGroup, kind: PatternKind) => {
    appGroup.patternKind = kind;
  }

  private patternsOfKind = (appGroup: EditedAppsGroup, kind: PatternKind): string[] => {
    if (kind == "include") {
      appGroup.includePatterns = appGroup.includePatterns ?? [];
      return appGroup.includePatterns;
    }
    appGroup.excludePatterns = appGroup.excludePatterns ?? [];
    return appGroup.excludePatterns;
  }

  /**
   * Adds the pattern being typed to the patterns of the selected kind. Several ones can be pasted at once,
   * separated by commas.
   */
  commitPatternDraft = (appGroup: EditedAppsGroup) => {
    let typedPatterns = parsePatterns(appGroup.patternDraft);
    appGroup.patternDraft = "";

    let patterns = this.patternsOfKind(appGroup, this.patternKind(appGroup));
    let added = false;
    for (let pattern of typedPatterns) {
      if (!patterns.includes(pattern)) {
        patterns.push(pattern);
        added = true;
      }
    }

    if (added) {
      this.membersRulesChanged(appGroup);
    }
  }

  // Enter and a comma add the pattern being typed, and Backspace in an empty input removes the last one
  patternKeydown = (appGroup: EditedAppsGroup, event: KeyboardEvent) => {
    let draft = appGroup.patternDraft ?? "";

    if (event.key == "Enter" || (event.key == "," && !endsInsideBrackets(draft))) {
      event.preventDefault();
      this.commitPatternDraft(appGroup);
    } else if (event.key == "Backspace" && draft === "") {
      let patterns = this.patternsOfKind(appGroup, this.patternKind(appGroup));
      if (patterns.length > 0) {
        patterns.pop();
        this.membersRulesChanged(appGroup);
      }
    }
  }

  removePattern = (appGroup: EditedAppsGroup, kind: PatternKind, pattern: string) => {
    let patterns = this.patternsOfKind(appGroup, kind);
    patterns.splice(patterns.indexOf(pattern), 1);
    this.membersRulesChanged(appGroup);
  }

  // Why the patterns would be refused by the appliance, or undefined when they are fine
  membersRulesError = (appGroup: EditedAppsGroup): string => {
    for (let patterns of [appGroup.includePatterns ?? [], appGroup.excludePatterns ?? []]) {
      if (patterns.length > MAX_PATTERNS_COUNT) {
        return `At most ${MAX_PATTERNS_COUNT} patterns are allowed.`;
      }
      if (patterns.filter((pattern) => pattern.length > MAX_PATTERN_LENGTH).length > 0) {
        return `The patterns are limited to ${MAX_PATTERN_LENGTH} characters.`;
      }
    }
    return undefined;
  }

  allRulesAreValid = (): boolean => {
    return this.appGroups.filter((appGroup) => this.membersRulesError(appGroup) != undefined).length == 0;
  }

  /**
   * Called as the user edits the patterns of an apps group: its rules are built from them, and what they match
   * is asked to the appliance once the user pauses.
   */
  membersRulesChanged = (appGroup: EditedAppsGroup) => {
    let include = [...(appGroup.includePatterns ?? [])];
    let exclude = [...(appGroup.excludePatterns ?? [])];

    if (include.length == 0 && exclude.length == 0) {
      delete appGroup.members_rules;
    } else {
      appGroup.members_rules = {include: include, exclude: exclude};
    }

    clearTimeout(this.previewTimers.get(appGroup));
    this.previewTimers.delete(appGroup);

    // Without any include pattern nothing is matched, there is no need to ask
    if (include.length == 0 || this.membersRulesError(appGroup) != undefined) {
      appGroup.rulesPreview = undefined;
      if (include.length == 0) {
        appGroup.matched_vms = [];
      }
      return;
    }

    appGroup.rulesPreview = {...(appGroup.rulesPreview ?? {failed: false}), loading: true};
    this.previewTimers.set(appGroup, setTimeout(() => this.previewMembersRules(appGroup), PREVIEW_DELAY_MS));
  }

  private previewMembersRules = (appGroup: EditedAppsGroup) => {
    this.previewTimers.delete(appGroup);

    // Only the answer to the last patterns typed is kept, an earlier one may come back after it
    let requestId = (this.previewRequestIds.get(appGroup) ?? 0) + 1;
    this.previewRequestIds.set(appGroup, requestId);

    let rules = appGroup.members_rules;
    this.netscopeService.previewAppsGroupsRules(rules.include, rules.exclude).subscribe((response) => {
      if (this.previewRequestIds.get(appGroup) != requestId) {
        return;
      }

      if (!(response instanceof Success)) {
        appGroup.rulesPreview = {loading: false, failed: true};
        return;
      }

      let preview: AppsGroupsRulesPreview = response.element;
      appGroup.rulesPreview = {loading: false, failed: false, total: preview.total, members: preview.members};

      // The members list follows the patterns as they are typed when the preview holds all the matched
      // resources. Otherwise it is refreshed once the file is saved, the appliance computing the members then.
      if (preview.members.length == preview.total) {
        appGroup.matched_vms = preview.members
          .map((member) => ({uuid: member.uuid, name: this.resourceLabel(member.uuid)}));
      }
    });
  }

  previewIsTruncated = (appGroup: EditedAppsGroup): boolean => {
    let preview = appGroup.rulesPreview;
    return preview?.total != undefined && preview.members.length < preview.total;
  }

  /**
   * How many VMs and hosts the patterns match: the last preview as they were typed, or the members the
   * appliance computed when the file was saved. Undefined while a preview is on its way.
   */
  matchCount = (appGroup: EditedAppsGroup): number => {
    let preview = appGroup.rulesPreview;

    if (preview?.loading) {
      return undefined;
    }
    if (preview?.total != undefined) {
      return preview.total;
    }
    return (appGroup.matched_vms ?? []).length;
  }

  matchCountLabel = (appGroup: EditedAppsGroup): string => {
    let count = this.matchCount(appGroup);

    if (count == undefined) {
      return "matching…";
    }
    if (count == 0) {
      return "no match";
    }
    return count == 1 ? "1 match" : `${count} matches`;
  }

  // First matched VMs and hosts, listed in the popover of the match count
  previewedMembers = (appGroup: EditedAppsGroup): { name: string, type: string }[] => {
    let members = appGroup.rulesPreview?.total != undefined
      ? appGroup.rulesPreview.members
      : (appGroup.matched_vms ?? []).map((member) => ({name: this.resourceLabel(member.uuid), type: this.memberType(member.uuid)}));

    return members.slice(0, PREVIEWED_NAMES_COUNT);
  }

  previewedOthersCount = (appGroup: EditedAppsGroup): number => {
    return Math.max(0, (this.matchCount(appGroup) ?? 0) - PREVIEWED_NAMES_COUNT);
  }

  togglePreview = (appGroup: EditedAppsGroup) => {
    if (!this.matchCount(appGroup)) {
      return;
    }
    this.previewedGroup = this.previewedGroup === appGroup ? undefined : appGroup;
  }

  // Members matched by the rules that the group does not list itself: the picked ones are shown once
  matchedOnlyMembers = (appGroup: EditedAppsGroup): any[] => {
    let pickedUuids = new Set((appGroup.vms ?? []).map((vm) => vm.uuid));
    return (appGroup.matched_vms ?? []).filter((member) => !pickedUuids.has(member.uuid));
  }

  // Members of the group itself, the ones it inherits from the apps groups it includes aside
  ownMembersCount = (appGroup: EditedAppsGroup): number => {
    return (appGroup.vms ?? []).length + this.matchedOnlyMembers(appGroup).length;
  }

  /**
   * A member matched by the rules cannot be removed as a picked one is: the rules would match it again. Its
   * exact name is added to the exclude patterns instead.
   */
  excludeMatchedMember = (appGroup: EditedAppsGroup, member) => {
    let pattern = exactNamePattern(this.resourcesByUuid.get(member.uuid)?.name ?? member.name);
    let exclude = this.patternsOfKind(appGroup, "exclude");

    if (!exclude.includes(pattern)) {
      exclude.push(pattern);
    }

    appGroup.matched_vms = (appGroup.matched_vms ?? []).filter((matched) => matched.uuid !== member.uuid);
    this.membersRulesChanged(appGroup);
  }

  // ------------------------------------------- Apps groups included in others

  /**
   * Apps groups of a file, the ones being edited when it is the displayed file: an apps group can include one
   * of another file, and the groups of the other files are loaded read-only, next to the edited ones.
   */
  groupsOfFile = (file: string): any[] => {
    if (file == undefined || file == this.selectedFile) {
      return this.appGroups;
    }

    return this.appsGroupsByFile.get(file) ?? [];
  }

  // Apps group an inclusion points to, in the displayed file when it names none
  appGroupByReference = (name: string, file?: string) => {
    return this.groupsOfFile(file ?? this.selectedFile)
      .find((appGroup) => appGroup.name == name);
  }

  appGroupByName = (name: string) => {
    return this.appGroupByReference(name);
  }

  // File an inclusion points into: the displayed one when it names none
  includedFile = (included): string => {
    return included?.file ?? this.selectedFile;
  }

  // Label of an inclusion, the file being spelled out only when it is not the displayed one
  includedLabel = (included): string => {
    return included?.file == undefined ? included?.name : `${included.file} / ${included.name}`;
  }

  // Key under which an apps group is met while the inclusions are walked, files included
  appGroupKey = (name: string, file: string): string => {
    return `${file}/${name}`;
  }

  /**
   * Apps groups that can be included in the given one, taken from every apps groups file: itself is left out,
   * and so are the ones that already include it, directly or through another group, as the two would then
   * contain each other. The groups of the displayed file come first, they are the ones usually looked for.
   */
  includableAppGroups = (appGroup): any[] => {
    let candidates = [];

    let files = [this.selectedFile]
      .concat(Array.from(this.appsGroupsByFile.keys()).filter((file) => file != this.selectedFile).sort());

    for (let file of files) {
      for (let candidate of this.groupsOfFile(file)) {
        if (candidate === appGroup) {
          continue;
        }
        if (this.isIncludedIn(candidate, file, appGroup.name, this.selectedFile, new Set())) {
          continue;
        }
        if ((appGroup.included_apps_groups ?? [])
          .filter((included) => included.name == candidate.name
            && this.includedFile(included) == file).length > 0) {
          continue;
        }

        candidates.push({name: candidate.name, file: file});
      }
    }

    return candidates;
  }

  // Whether an apps group includes the named one, directly or through the groups it includes itself
  isIncludedIn = (appGroup, appGroupFile: string, name: string, file: string, visited: Set<string>): boolean => {
    if (appGroup == undefined || visited.has(this.appGroupKey(appGroup.name, appGroupFile))) {
      return false;
    }
    visited.add(this.appGroupKey(appGroup.name, appGroupFile));

    for (let included of appGroup.included_apps_groups ?? []) {
      // An inclusion naming no file points inside the file holding the group that carries it
      let includedFile = included.file ?? appGroupFile;

      if (included.name == name && includedFile == file) {
        return true;
      }
      if (this.isIncludedIn(this.appGroupByReference(included.name, includedFile), includedFile,
                            name, file, visited)) {
        return true;
      }
    }

    return false;
  }

  includeAppGroup = (appGroup, name: string, file: string) => {
    if (appGroup.included_apps_groups == undefined) {
      appGroup.included_apps_groups = [];
    }
    if (appGroup.included_apps_groups
      .filter((included) => included.name == name && this.includedFile(included) == file).length > 0) {
      return;
    }

    // An inclusion inside the displayed file names no file: the file can then be renamed or copied without
    // its groups pointing back at their former file
    let inclusion: {name: string, file?: string} = {name: name};
    if (file != this.selectedFile) {
      inclusion.file = file;
    }

    appGroup.included_apps_groups.push(inclusion);
  }

  removeIncludedAppGroup = (appGroup, included) => {
    appGroup.included_apps_groups = appGroup.included_apps_groups
      .filter((candidate) => !(candidate.name === included.name
        && this.includedFile(candidate) == this.includedFile(included)));
    this.refreshAppGroups();
  }

  /**
   * Members of an apps group, the ones it holds itself, the ones its rules match and the ones it inherits from
   * the apps groups it includes, whatever the file they belong to. A group already met is not walked twice, so that a cycle
   * cannot loop.
   */
  effectiveMembers = (appGroup, file?: string, visited: Set<string> = new Set()): any[] => {
    file = file ?? this.selectedFile;

    if (appGroup == undefined || visited.has(this.appGroupKey(appGroup.name, file))) {
      return [];
    }
    visited.add(this.appGroupKey(appGroup.name, file));

    let membersByUuid: Map<string, any> = new Map();

    for (let member of appGroup.vms ?? []) {
      membersByUuid.set(member.uuid, member);
    }

    for (let member of appGroup.matched_vms ?? []) {
      if (!membersByUuid.has(member.uuid)) {
        membersByUuid.set(member.uuid, member);
      }
    }

    for (let included of appGroup.included_apps_groups ?? []) {
      let includedFile = included.file ?? file;

      for (let member of this.effectiveMembers(this.appGroupByReference(included.name, includedFile),
                                               includedFile, visited)) {
        if (!membersByUuid.has(member.uuid)) {
          membersByUuid.set(member.uuid, member);
        }
      }
    }

    return Array.from(membersByUuid.values());
  }

  // Whether an inclusion points to an apps group that is not there anymore: its file was deleted, or the
  // group was renamed or removed from a file other than the displayed one
  includedIsMissing = (included): boolean => {
    return this.appGroupByReference(included.name, this.includedFile(included)) == undefined;
  }

  allNamesAreValid = (): boolean => {
    return this.appGroups
      .filter((appGroup) => !this.checkAppGroupName(appGroup)).length == 0;
  }

  save = () => {
    // A generated file is rewritten by the appliance: it is never written back from here
    if (!this.isEditableFile() || !this.allNamesAreValid() || !this.allRulesAreValid()) {
      return;
    }

    this.isPushing = true;
    this.saveFailed = false;

    // The whole file is written back
    let curatedAppsGroups = this.appsGroupsData.apps_groups.map((appGroup) => {
      let curatedAppGroup = {
        name: appGroup.name,
        vms: appGroup.vms.map((vm) => {
          return {uuid: vm.uuid};
        }),
        metadata: appGroup.metadata != undefined ? appGroup.metadata : {}
      };

      if ((appGroup.included_apps_groups ?? []).length > 0) {
        curatedAppGroup["included_apps_groups"] = appGroup.included_apps_groups
          .map((included) => {
            // An inclusion inside the displayed file names no file, only the ones pointing elsewhere do
            return included.file == undefined
              ? {name: included.name}
              : {name: included.name, file: included.file};
          });
      }

      // The members the rules match are not sent back: the appliance computes them again when saving
      if ((appGroup.members_rules?.include ?? []).length > 0 || (appGroup.members_rules?.exclude ?? []).length > 0) {
        curatedAppGroup["members_rules"] = {
          include: appGroup.members_rules.include ?? [],
          exclude: appGroup.members_rules.exclude ?? [],
        };
      }

      return curatedAppGroup;
    });

    let appsGroupsPayload = {
      apps_groups: curatedAppsGroups
    };

    let savedFile = this.selectedFile;
    this.netscopeService.pushAppsGroups(savedFile, appsGroupsPayload).subscribe((response: Response<object>) => {
      this.isPushing = false;

      if (!(response instanceof Success)) {
        this.saveFailed = true;
        return;
      }

      this.appsGroupsSaved.emit(savedFile);

      // The appliance computed the members matched by the rules while saving: the file is read back to list
      // them, unless another file was selected meanwhile
      this.netscopeService.getAppsGroups(savedFile).subscribe((appsGroupsDataResponse: Response<AppsGroupsData>) => {
        if (savedFile == this.selectedFile && appsGroupsDataResponse instanceof Success) {
          this.appsGroupsData = appsGroupsDataResponse.element;
          this.refreshAppGroups();
        }
      });
    });
  }

  // ------------------------------------------------- Display of the groups

  isHidden = (appGroup): boolean => {
    return this.hiddenAppsGroups.has(appGroup.name);
  }

  toggleHidden = (appGroup) => {
    if (this.hiddenAppsGroups.has(appGroup.name)) {
      this.hiddenAppsGroups.delete(appGroup.name);
    } else {
      this.hiddenAppsGroups.add(appGroup.name);
    }
    this.viewStateChanged.emit();
  }

  getMode = (appGroup): AppsGroupMembersDisplayMode => {
    if (this.appGroupStates.has(appGroup.name)) {
      return this.appGroupStates.get(appGroup.name).mode;
    }
    return defaultDisplayMode(appGroup);
  }

  // The display state of an apps group is created on the first choice made on it, and kept afterwards
  getState = (appGroup): AppGroupState => {
    if (!this.appGroupStates.has(appGroup.name)) {
      let appGroupState: AppGroupState = new AppGroupState();
      appGroupState.mode = defaultDisplayMode(appGroup);
      this.appGroupStates.set(appGroup.name, appGroupState);
    }

    let appGroupState = this.appGroupStates.get(appGroup.name);
    appGroupState.appGroup = appGroup;
    appGroupState.uuid = appGroup.uuid;
    appGroupState.name = appGroup.name;

    return appGroupState;
  }

  setMode = (appGroup, mode: AppsGroupMembersDisplayMode) => {
    this.getState(appGroup).mode = mode;
    this.applyMode(appGroup, mode);
    this.viewStateChanged.emit();
  }

  /**
   * Whether the flows exchanged between the members of the apps group are drawn. They are hidden by default,
   * as a group usually talks to itself a lot and those links would bury the flows between groups.
   */
  showsInternalFlows = (appGroup): boolean => {
    return this.appGroupStates.get(appGroup.name)?.showInternalFlows ?? false;
  }

  toggleInternalFlows = (appGroup) => {
    let appGroupState = this.getState(appGroup);
    appGroupState.showInternalFlows = !appGroupState.showInternalFlows;
    this.viewStateChanged.emit();
  }

  /**
   * A display mode is nothing more than a preset over the display choices of the apps group (see
   * applyDisplayMode): it is applied once, and the user is then free to show or fold any member by clicking its
   * label, any included apps group the same way, and to draw the inside traffic or not.
   */
  applyMode = (appGroup, mode: AppsGroupMembersDisplayMode) => {
    applyDisplayMode(this.getState(appGroup), mode, this.effectiveMembers(appGroup, this.selectedFile),
                     appGroup.included_apps_groups ?? [], this.vmVisibilityStates, this.memberRoles);
  }

  /**
   * Whether a member is shown as a node of its own in the view. Members are folded into the node of their
   * apps group until a mode or the user shows them, which is what the 'summarized' mode goes back to.
   */
  isVmVisible = (appGroup, vm): boolean => {
    return this.vmVisibilityStates.get(memberVisibilityKey(appGroup.name, vm.uuid)) ?? false;
  }

  toggleVmVisibility = (appGroup, vm) => {
    this.vmVisibilityStates.set(memberVisibilityKey(appGroup.name, vm.uuid), !this.isVmVisible(appGroup, vm));
    this.viewStateChanged.emit();
  }

  /**
   * Whether an included apps group is drawn as a box of its own inside the group including it, which it is by
   * default. Folded, it gets no box and its members are drawn as the ones the group lists itself.
   */
  isIncludedVisible = (appGroup, included): boolean => {
    return !(this.appGroupStates.get(appGroup.name)?.hiddenIncludedAppsGroups.has(includedAppsGroupKey(included)) ?? false);
  }

  toggleIncludedVisibility = (appGroup, included) => {
    let hidden = this.getState(appGroup).hiddenIncludedAppsGroups;
    let key = includedAppsGroupKey(included);

    if (hidden.has(key)) {
      hidden.delete(key);
    } else {
      hidden.add(key);
    }
    this.viewStateChanged.emit();
  }

  // Members that took part in no flow at all are only shown by the 'show all' mode
  hasNoFlow = (vm): boolean => {
    return !this.memberRoles.has(vm.uuid);
  }
}
